/**
 * Convert store/service hours from America/Toronto wall time to the browser
 * timezone when that zone is US/Canada. Outside North America → Eastern (ET).
 *
 * Supports:
 * - [data-hours-tz-convert]  footer Online Service open/closed snippet
 * - [data-hours-tz-range]    contact-table (and similar) static hour ranges
 *
 * SSR fallback remains ET until this runs.
 */
(function () {
  'use strict';

  var SOURCE_TZ = 'America/Toronto';
  var FRIENDLY_TZ = {
    EST: 'ET',
    EDT: 'ET',
    CST: 'CT',
    CDT: 'CT',
    MST: 'MT',
    MDT: 'MT',
    PST: 'PT',
    PDT: 'PT',
    AST: 'AT',
    ADT: 'AT',
    NST: 'NT',
    NDT: 'NT',
    AKST: 'AKT',
    AKDT: 'AKT',
    HST: 'HT'
  };

  /** US + Canada IANA zones we localize into; anything else → ET */
  var NA_TIMEZONES = {
    /* Canada — Eastern */
    'America/Toronto': 1,
    'America/Montreal': 1,
    'America/Nipigon': 1,
    'America/Thunder_Bay': 1,
    'America/Iqaluit': 1,
    'America/Pangnirtung': 1,
    /* Canada — Atlantic / Newfoundland */
    'America/Halifax': 1,
    'America/Moncton': 1,
    'America/Glace_Bay': 1,
    'America/Goose_Bay': 1,
    'America/Blanc-Sablon': 1,
    'America/St_Johns': 1,
    /* Canada — Central */
    'America/Winnipeg': 1,
    'America/Rainy_River': 1,
    'America/Resolute': 1,
    'America/Rankin_Inlet': 1,
    'America/Regina': 1,
    'America/Swift_Current': 1,
    /* Canada — Mountain */
    'America/Edmonton': 1,
    'America/Cambridge_Bay': 1,
    'America/Yellowknife': 1,
    'America/Inuvik': 1,
    'America/Creston': 1,
    'America/Dawson_Creek': 1,
    'America/Fort_Nelson': 1,
    /* Canada — Pacific */
    'America/Vancouver': 1,
    'America/Whitehorse': 1,
    'America/Dawson': 1,
    /* US — Eastern */
    'America/New_York': 1,
    'America/Detroit': 1,
    'America/Kentucky/Louisville': 1,
    'America/Kentucky/Monticello': 1,
    'America/Indiana/Indianapolis': 1,
    'America/Indiana/Vincennes': 1,
    'America/Indiana/Winamac': 1,
    'America/Indiana/Marengo': 1,
    'America/Indiana/Petersburg': 1,
    'America/Indiana/Vevay': 1,
    'America/Indiana/Tell_City': 1,
    'America/Indiana/Knox': 1,
    /* US — Central */
    'America/Chicago': 1,
    'America/Menominee': 1,
    'America/North_Dakota/Center': 1,
    'America/North_Dakota/New_Salem': 1,
    'America/North_Dakota/Beulah': 1,
    /* US — Mountain */
    'America/Denver': 1,
    'America/Boise': 1,
    'America/Phoenix': 1,
    /* US — Pacific */
    'America/Los_Angeles': 1,
    /* US — Alaska / Hawaii */
    'America/Anchorage': 1,
    'America/Juneau': 1,
    'America/Sitka': 1,
    'America/Yakutat': 1,
    'America/Nome': 1,
    'America/Adak': 1,
    'America/Metlakatla': 1,
    'Pacific/Honolulu': 1
  };

  function getBrowserTimeZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
  }

  function getDisplayTimeZone() {
    var localTz = getBrowserTimeZone();
    if (localTz && NA_TIMEZONES[localTz]) return localTz;
    return SOURCE_TZ;
  }

  function getPartsInTimeZone(date, timeZone) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    var map = {};
    var parts = dtf.formatToParts(date);
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value;
    }
    return {
      year: +map.year,
      month: +map.month,
      day: +map.day,
      hour: +map.hour,
      minute: +map.minute,
      second: +map.second
    };
  }

  /** Wall clock in `timeZone` → Date (UTC instant). */
  function zonedWallTimeToDate(timeZone, year, month, day, hour, minute) {
    var utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
    var parts = getPartsInTimeZone(new Date(utcGuess), timeZone);
    var asIfUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);
    var offset = asIfUtc - utcGuess;
    var utc = Date.UTC(year, month - 1, day, hour, minute, 0) - offset;
    var parts2 = getPartsInTimeZone(new Date(utc), timeZone);
    var asIfUtc2 = Date.UTC(parts2.year, parts2.month - 1, parts2.day, parts2.hour, parts2.minute, 0);
    var offset2 = asIfUtc2 - utc;
    return new Date(Date.UTC(year, month - 1, day, hour, minute, 0) - offset2);
  }

  function parseHm(hm) {
    if (!hm || hm.indexOf(':') === -1) return null;
    var bits = hm.split(':');
    return { hour: parseInt(bits[0], 10), minute: parseInt(bits[1], 10) };
  }

  function formatInTimeZone(date, timeZone) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    var map = {};
    var parts = dtf.formatToParts(date);
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type !== 'literal') map[parts[i].type] = parts[i].value;
    }
    var hour = String(map.hour);
    var minute = map.minute;
    var period = String(map.dayPeriod || '').toLowerCase();
    if (minute === '00') return hour + period;
    return hour + ':' + minute + period;
  }

  function friendlyTzLabel(date, displayTz) {
    if (displayTz === SOURCE_TZ || displayTz === 'America/Montreal' || displayTz === 'America/Nipigon' || displayTz === 'America/Thunder_Bay') {
      return 'ET';
    }
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: displayTz,
      timeZoneName: 'short'
    });
    var parts = dtf.formatToParts(date);
    var abbr = '';
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type === 'timeZoneName') {
        abbr = parts[i].value;
        break;
      }
    }
    return FRIENDLY_TZ[abbr] || abbr || 'ET';
  }

  function addDays(parts, days) {
    var dt = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days, 12, 0, 0));
    return { year: dt.getUTCFullYear(), month: dt.getUTCMonth() + 1, day: dt.getUTCDate() };
  }

  function parseDateParts(dateStr) {
    if (!dateStr || dateStr.indexOf('-') === -1) return null;
    var bits = dateStr.split('-');
    return {
      year: parseInt(bits[0], 10),
      month: parseInt(bits[1], 10),
      day: parseInt(bits[2], 10)
    };
  }

  function convertRoot(root) {
    var sourceTz = root.getAttribute('data-source-tz') || SOURCE_TZ;
    var displayTz = getDisplayTimeZone();
    var cutoff = parseInt(root.getAttribute('data-cutoff-hour') || '18', 10);
    var omitName = root.getAttribute('data-omit-name') === 'true';
    var storeName = root.getAttribute('data-store-name') || 'Online Service';

    var nowParts = getPartsInTimeZone(new Date(), sourceTz);
    var useTomorrow = nowParts.hour >= cutoff;
    var dayLabel = useTomorrow ? 'tomorrow' : 'today';
    var dayParts = useTomorrow ? addDays(nowParts, 1) : { year: nowParts.year, month: nowParts.month, day: nowParts.day };

    var closed = root.getAttribute(useTomorrow ? 'data-tomorrow-closed' : 'data-today-closed') === 'true';
    var openHm = parseHm(root.getAttribute(useTomorrow ? 'data-tomorrow-open' : 'data-today-open'));
    var closeHm = parseHm(root.getAttribute(useTomorrow ? 'data-tomorrow-close' : 'data-today-close'));

    var statusEl = root.querySelector('.time_calc_block__status');
    var rangeEl = root.querySelector('.time_calc_block__range');
    var tzEl = root.querySelector('.time_calc_block__tz');

    if (statusEl) {
      if (omitName) {
        statusEl.textContent = (closed ? 'Closed' : 'Open') + ' ' + dayLabel;
      } else {
        statusEl.textContent = storeName + ' ' + (closed ? 'closed' : 'open') + ' ' + dayLabel;
      }
    }

    if (closed || !openHm || !closeHm) {
      if (rangeEl) rangeEl.textContent = 'Store Hours';
      if (tzEl) tzEl.textContent = '';
      return;
    }

    var openDate = zonedWallTimeToDate(sourceTz, dayParts.year, dayParts.month, dayParts.day, openHm.hour, openHm.minute);
    var closeDate = zonedWallTimeToDate(sourceTz, dayParts.year, dayParts.month, dayParts.day, closeHm.hour, closeHm.minute);

    if (rangeEl) {
      rangeEl.textContent = formatInTimeZone(openDate, displayTz) + ' - ' + formatInTimeZone(closeDate, displayTz);
    }
    if (tzEl) {
      var label = friendlyTzLabel(openDate, displayTz);
      tzEl.textContent = label ? ' ' + label : ' ET';
    }
  }

  /** Static open–close ranges (contact-table weekly rows, regular hours, specials). */
  function convertRange(el) {
    var sourceTz = el.getAttribute('data-source-tz') || SOURCE_TZ;
    var displayTz = getDisplayTimeZone();
    var openHm = parseHm(el.getAttribute('data-open'));
    var closeHm = parseHm(el.getAttribute('data-close'));
    var dayParts = parseDateParts(el.getAttribute('data-date'));
    var textEl = el.querySelector('.hours-tz-range__text');
    var tzEl = el.querySelector('.hours-tz-range__tz');

    if (!openHm || !closeHm) return;
    if (!dayParts) {
      var nowParts = getPartsInTimeZone(new Date(), sourceTz);
      dayParts = { year: nowParts.year, month: nowParts.month, day: nowParts.day };
    }

    var openDate = zonedWallTimeToDate(sourceTz, dayParts.year, dayParts.month, dayParts.day, openHm.hour, openHm.minute);
    var closeDate = zonedWallTimeToDate(sourceTz, dayParts.year, dayParts.month, dayParts.day, closeHm.hour, closeHm.minute);

    if (textEl) {
      textEl.textContent = formatInTimeZone(openDate, displayTz) + ' - ' + formatInTimeZone(closeDate, displayTz);
    }
    if (tzEl) {
      tzEl.textContent = friendlyTzLabel(openDate, displayTz) || 'ET';
    }
  }

  function run() {
    if (!window.Intl || !Intl.DateTimeFormat) return;
    var i;
    var convertNodes = document.querySelectorAll('[data-hours-tz-convert]');
    for (i = 0; i < convertNodes.length; i++) {
      try {
        convertRoot(convertNodes[i]);
      } catch (e) {
        /* leave SSR ET fallback */
      }
    }
    var rangeNodes = document.querySelectorAll('[data-hours-tz-range]');
    for (i = 0; i < rangeNodes.length; i++) {
      try {
        convertRange(rangeNodes[i]);
      } catch (e2) {
        /* leave SSR ET fallback */
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
